import React, { useEffect, useRef, useState } from "react";
import { Bell, RefreshCw, CheckCheck, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { appAlert } from "../../../utils/appPopup";
import { ensureWarehouseContext } from "../../../services/warehouseContext.service";
import { getAuthSession, getAuthUser } from "../../AdminAuth/components/authStore";
import {
  WarehouseNotificationsService,
  mapWarehouseNotificationToRoute,
  isWarehouseNotificationRoute,
} from "../../../services/warehouseNotifications.service";

function timeAgo(value) {
  if (!value) return "";
  const date = new Date(value);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);

  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  return `${Math.floor(diff / 86400)} day ago`;
}

function getTitle(item) {
  return item?.title || item?.type || "Notification";
}

function getMessage(item) {
  return item?.body || item?.message || "You have a new notification.";
}

function isUnread(item) {
  return !(item?.is_read || item?.isRead);
}

function getNotificationWarehouseId(item = {}) {
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};

  return String(
    item?.warehouseId ||
      item?.warehouse_id ||
      meta?.warehouseId ||
      meta?.warehouse_id ||
      meta?.selectedWarehouseId ||
      meta?.assignedWarehouseId ||
      ""
  ).trim();
}

function canOpenWarehousePanel(item = {}) {
  const session = getAuthSession() || {};
  const user = getAuthUser() || {};
  const dashboardAccess = session?.dashboardAccess || {};
  const warehouseAccess = session?.warehouseAccess || {};
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  const role = String(user?.role || "").toLowerCase();

  const hasWarehousePanelAccess =
    role === "super_admin" ||
    !!dashboardAccess?.canOpenWarehouseDashboard ||
    !!user?.canAccessWarehouseDashboard ||
    !!user?.canSwitchToWarehouseDashboard ||
    permissions.includes("warehouse.panel.access") ||
    permissions.includes("wh.panel.access");

  if (!hasWarehousePanelAccess) return false;

  const notificationWarehouseId = getNotificationWarehouseId(item);
  if (!notificationWarehouseId) return true;

  if (
    role === "super_admin" ||
    warehouseAccess?.mode === "all" ||
    !!warehouseAccess?.canSwitchAnyWarehouse
  ) {
    return true;
  }

  const allowedWarehouseIds = [
    ...(Array.isArray(warehouseAccess?.warehouseIds) ? warehouseAccess.warehouseIds : []),
    warehouseAccess?.assignedWarehouseId,
    session?.selectedWarehouseId,
  ]
    .filter(Boolean)
    .map((id) => String(id));

  return allowedWarehouseIds.includes(notificationWarehouseId);
}

function showWarehousePermissionAlert() {
  appAlert("You do not have permission to access the warehouse panel.", {
    title: "Permission denied",
    danger: true,
    confirmText: "OK",
  });
}

async function allowWarehouseRedirect(item, targetRoute) {
  if (!isWarehouseNotificationRoute(targetRoute)) return true;

  if (!canOpenWarehousePanel(item)) {
    showWarehousePermissionAlert();
    return false;
  }

  const notificationWarehouseId = getNotificationWarehouseId(item);
  try {
    await ensureWarehouseContext(notificationWarehouseId);
    return true;
  } catch (err) {
    console.error("Failed to establish warehouse context:", err);
    appAlert(
      err?.message || "Unable to open the warehouse right now. Please try again.",
      {
        title: "Warehouse switch failed",
        danger: true,
        confirmText: "OK",
      }
    );
    return false;
  }
}

export default function WarehouseNotificationsBell() {
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  async function loadUnreadCount() {
    try {
      const res = await WarehouseNotificationsService.unreadCount();
      setUnreadCount(Number(res?.count || 0));
    } catch (err) {
      console.error("Failed to load unread count:", err);
      setUnreadCount(0);
    }
  }

  async function loadNotifications() {
    try {
      setLoading(true);

      const res = await WarehouseNotificationsService.list({
        page: 1,
        limit: 20,
      });

      const list = Array.isArray(res?.items) ? res.items : [];

      // Dropdown me only unread notifications show hongi
      setItems(list.filter(isUnread));
    } catch (err) {
      console.error("Failed to load warehouse notifications:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleOpen() {
    const next = !open;
    setOpen(next);

    if (!open) {
      await Promise.all([loadUnreadCount(), loadNotifications()]);
    }
  }

  async function handleMarkRead(id) {
    try {
      await WarehouseNotificationsService.markRead(id);

      // Read hone ke baad dropdown se remove
      setItems((prev) => prev.filter((item) => item.id !== id));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark warehouse notification as read:", err);
    }
  }

  async function handleMarkAllRead() {
    try {
      setMarkingAll(true);
      await WarehouseNotificationsService.markAllRead();

      // Read all ke baad dropdown empty
      setItems([]);
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all warehouse notifications as read:", err);
    } finally {
      setMarkingAll(false);
    }
  }

  async function handleNotificationClick(item) {
    const targetRoute = mapWarehouseNotificationToRoute(item);

    if (!(await allowWarehouseRedirect(item, targetRoute))) {
      setOpen(false);
      return;
    }

    const wasUnread = isUnread(item);

    try {
      if (item?.id && wasUnread) {
        await WarehouseNotificationsService.markRead(item.id);
      }
    } catch (err) {
      console.error("Failed to mark warehouse notification as read:", err);
    } finally {
      // Click ke baad dropdown se remove
      if (item?.id) {
        setItems((prev) => prev.filter((n) => n.id !== item.id));
      }

      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      setOpen(false);
      navigate(targetRoute);
    }
  }

  function handleViewAll() {
    setOpen(false);
    navigate("/admin/dashboard/notifications");
  }

  useEffect(() => {
    loadUnreadCount();
  }, []);

  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current?.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={handleOpen}
        className="relative flex h-11 w-11 items-center justify-center rounded-full border border-[#f2c6cc] bg-[#fff8f9] text-[#d86a78] transition hover:bg-white"
        title="Warehouse notifications"
      >
        <Bell size={18} />

        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#d86a78] px-1 text-[11px] font-bold text-white shadow-sm">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 top-[calc(100%+12px)] z-50 w-[430px] max-w-[calc(100vw-24px)] overflow-hidden rounded-[22px] border border-[#f2c6cc] bg-white shadow-[0_22px_60px_rgba(15,23,42,0.16)]">
          <div className="border-b border-[#f2c6cc] bg-[#fff8f9] px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-[15px] font-bold text-slate-800">
                  Warehouse Notifications
                </h3>
                <p className="mt-0.5 text-[12px] text-slate-500">
                  {unreadCount > 0
                    ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
                    : "No unread notifications"}
                </p>
              </div>

              <button
                type="button"
                onClick={loadNotifications}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#f2c6cc] bg-white text-slate-500 transition hover:text-[#d86a78]"
                title="Refresh"
              >
                <RefreshCw size={15} />
              </button>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleMarkAllRead}
                disabled={markingAll || unreadCount === 0}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#d86a78] px-3 py-1.5 text-[12px] font-semibold text-white transition hover:bg-[#c85d6a] disabled:cursor-not-allowed disabled:opacity-55"
              >
                <CheckCheck size={14} />
                {markingAll ? "Reading..." : "Read all"}
              </button>

              <button
                type="button"
                onClick={handleViewAll}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#f2c6cc] bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 transition hover:text-[#d86a78]"
              >
                <ExternalLink size={13} />
                View all
              </button>
            </div>
          </div>

          <div className="max-h-[460px] overflow-y-auto">
            {loading ? (
              <div className="p-5 text-sm text-slate-400">Loading...</div>
            ) : items.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#fff1f3] text-[#d86a78]">
                  <Bell size={18} />
                </div>
                <p className="mt-3 text-sm font-semibold text-slate-700">
                  No unread notifications
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Read notifications are available in View all.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {items.map((item) => (
                  <button
                    key={item?.id}
                    type="button"
                    onClick={() => handleNotificationClick(item)}
                    className="block w-full px-4 py-3.5 text-left transition hover:bg-[#fff8f9]"
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-[#d86a78]" />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <h4 className="line-clamp-1 text-[14px] font-bold text-slate-800">
                            {getTitle(item)}
                          </h4>

                          <span className="shrink-0 text-[11px] text-slate-400">
                            {timeAgo(item?.created_at || item?.createdAt)}
                          </span>
                        </div>

                        <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-slate-500">
                          {getMessage(item)}
                        </p>

                        <p className="mt-2 text-[12px] font-semibold text-[#d86a78]">
                          Click to open and mark as read
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}